<template>
  <b-modal
    :id="modalId"
    v-model="localIsActive"
    size="xl"
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    @hide="onHide"
  >
    <template #modal-title>
      <h4 class="mb-0 text-white">Empleados</h4>
    </template>

    <div class="d-flex justify-content-end mb-1">
      <div class="col-4">
        <b-form-input
          v-model="searchQuery"
          placeholder="Empleados externos: Buscar por SOEID, Nombre"
        />
      </div>
      <div class="col-auto px-0">
        <b-button
          variant="primary"
          class="w-100"
          :disabled="loading"
          @click="addUsers()"
        >
          <feather-icon icon="PlusIcon" class="mr-25"/>
          <span class="text-nowrap">Agregar</span>
        </b-button>
      </div>
    </div>

    <b-table
      ref="refListTable"
      class="position-relative mt-2"
      :items="fetchList"
      responsive
      :fields="tableColumns"
      primary-key="id"
      :sort-by.sync="sortBy"
      show-empty
      :empty-text="$t('no_record_found')"
      :sort-desc.sync="isSortDirDesc"
      selectable
      select-mode="multi"
      @row-selected="onRowSelected"
    >
      <template #head(selected)>
        <b-form-checkbox
          :checked="allSelected"
          :indeterminate="indeterminate"
          @change="toggleSelectAll"
        />
      </template>

      <template #cell(selected)="data">
        <b-form-checkbox
          :checked="isRowSelected(data.item)"
          @change="toggleRowSelection(data.index, $event)"
        />
      </template>

      <template #head()="data">
        <span>{{ data.label }}</span>
      </template>
    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <Pagination
            data-cy="pagination"
            :totalList="totalList"
            :currentPage="currentPage"
            @updatePagination="update"
            :perPage="perPage"
          >
          </Pagination>
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import axios from '@/libs/axios';
import store from '@/store';
import {useToast} from "vue-toastification/composition";
import {computed, onMounted, ref, watch} from '@vue/composition-api';
import Filters from './Filters.vue';
import Pagination from "@/components/Pagination.vue";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import {useFullscreenModalTransport} from './composables/useFullscreenModalTransport';

export default {
  props: {
    branch: {
      type: Object
    },
    operationalSecurityCrisis: {
      type: Object
    },
    showModal: {
      type: Boolean
    }
  },
  components: {
    Filters,
    Pagination,
    ToastificationContent
  },
  setup(props, {emit}) {
    //COMPOSABLES
    const toast = useToast();

    //DATA
    const modalId = computed(() => `crisis-users-rh-additional-${props.branch?.id || props.branch?.id_unit || 'modal'}`);
    const currentPage = ref(1);
    const isSortDirDesc = ref(true);
    const loading = ref(false);
    const perPage = ref(7);
    const perPageOptions = [10, 25, 50, 100];
    const refListTable = ref(null);
    const searchQuery = ref('');
    const selectedRows = ref([]);
    const selectedStates = ref([])
    const sortBy = ref('id');

    const tableColumns = [
      {key: "selected", label: "", thStyle: {width: "50px"}},
      {key: 'soeid', label: 'SOEID'},
      {key: 'name', label: 'Nombre'},
      {key: 'first_name', label: 'Apellido paterno',},
      {key: 'second_name', label: 'Apellido materno'},
      {key: 'email', label: 'Email'},
      {
        key: 'external_user',
        label: 'Externo',
        formatter: (value, key, item) => (value ? 'Sí' : 'No')
      },
      {key: 'unit.description', label: 'REMS'}
    ];

    const totalList = ref(0);

    const usersSelected = ref([]);

    //COMPUTED
    const allSelected = ref(false);

    const dataMeta = computed(() => {
      const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
      return {
        from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalList.value,
      }
    });

    const indeterminate = computed(() => (selectedRows.value.length > 0));

    const localIsActive = computed({
      get() {
        return props.showModal;
      },
      set(value) {
        emit("update:isActive", value);
      },
    });

    useFullscreenModalTransport(modalId, localIsActive);

    const municipalityOptions = computed(() => store.getters['municipality/getSelectedItems']);

    const regionOptions = computed(() => store.getters['region/getSelectedItems'])

    //METHODS
    const addUsers = () => {
      if (selectedRows.value.length === 0) {
        return
      }

      const addUsersRh = (data = null) => {
        if (data) {
          selectedRows.value = data;
        }

        selectedRows.value.forEach((row) => {
          const dataRow = {
            ...row,
            id_unit: props.branch.id_unit,
            id_user_rh: row.id,
            position: (row.additional_info) ? row.additional_info.position : null,
            phone: (row.additional_info) ? row.additional_info.phone : null,
            emergency_phone: (row.additional_info) ? row.additional_info.emergency_phone : null,
            address: (row.additional_info) ? row.additional_info.address : null,
            district: (row.additional_info) ? row.additional_info.district : null,
            municipality: (row.additional_info) ? row.additional_info.municipality : null,
            call_tree_status: (row.additional_info) ? row.additional_info.call_tree_status : null,
            contacted_by: (row.additional_info) ? row.additional_info.contacted_by : null,
            affects: []
          }

          usersSelected.value.push(dataRow);
        });

        if (usersSelected.value.length) {
          (async () => await saveUsersRh())();
        }
      }

      if (allSelected.value) {
        fetchList({max: -1}, addUsersRh);
      } else {
        addUsersRh();
      }
    }

    const saveUsersRh = async () => {
      loading.value = true;

      const payload = {
        id_operational_security_crisis: props.operationalSecurityCrisis.id,
        users: usersSelected.value
      }

      await axios.post('/v1/operational-security-crisis/users-rh', payload)
        .then(response => {
          const {data} = response;

          emit('save-employee-additional-success', data.data);

          toast({
            component: ToastificationContent,
            props: {
              title: data.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          });

          onHide();
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        })
        .finally(() => {
          loading.value = false;
        });
    }

    const fetchList = (ctx, callback) => {
      const excludedUsers = props.operationalSecurityCrisis.units_users_rh.filter(
        uurh => (uurh.id_unit === props.branch.id_unit)
      )
        .map(uurh => uurh.id_user_rh);

      const params = {
        q: searchQuery.value,
        max: (ctx.max !== -1) ? perPage.value : null,
        id_unit: props.branch.id_unit,
        id_crisis: props.operationalSecurityCrisis.id,
        excluded_users: excludedUsers,
        source: 'crisis_form',
        'page[n]': currentPage.value,
      }

      axios.get('v1/operational-security-crisis-users-rh', {params})
        .then(response => {
          callback(response.data.data)
          totalList.value = response.data.meta.total
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error obteniendo la información',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const isRowSelected = (item) => {
      return selectedRows.value.some((r) => r.id === item.id)
    }

    const onHide = () => {
      searchQuery.value = '';
      currentPage.value = 1;

      emit('update:isActive', false);
    }

    const onRowSelected = (items) => {
      selectedRows.value = items;
    }

    const refetchData = () => {
      refListTable.value.refresh()
    }

    const toggleRowSelection = (index, checked) => {
      if (checked) {
        refListTable.value.selectRow(index)
      } else {
        refListTable.value.unselectRow(index)
      }
    }

    const toggleSelectAll = (checked) => {
      allSelected.value = checked;
      if (checked) {
        refListTable.value.selectAllRows();
      } else {
        refListTable.value.clearSelected();
      }
    }

    const update = (data) => {
      currentPage.value = data;
    }

    //WATCHERS
    watch([selectedStates, currentPage, perPage, searchQuery], () => {
      refetchData();
    })

    return {
      currentPage,
      isSortDirDesc,
      loading,
      perPage,
      perPageOptions,
      refListTable,
      searchQuery,
      selectedStates,
      selectedRows,
      sortBy,
      tableColumns,
      totalList,

      allSelected,
      dataMeta,
      indeterminate,
      localIsActive,
      modalId,
      municipalityOptions,
      regionOptions,

      addUsers,
      fetchList,
      isRowSelected,
      onHide,
      onRowSelected,
      toggleRowSelection,
      toggleSelectAll,
      update
    }
  }
}
</script>

<style lang="scss" scoped>
.filters-row {
  gap: 10px;
}

.filter-field {
  min-width: 230px;
  max-width: 100%;
}
</style>
